"use client";

import React, { useId } from "react";
import { Crown, MapPin } from "lucide-react";
import { LgsLogo } from "modules/lgs/components/LgsLogo";
import { useFacetSearchParamsContract } from "modules/common/hooks/useFacetSearchParamsContract";
import {
  AVAILABILITY_CARD_CLASS,
  AVAILABILITY_HEADER_CLASS,
  AVAILABILITY_SIDES_CLASS,
  SideRow,
} from "./CityListItem";

export interface MergedStore {
  wotcId: number;
  slug: string;
  name: string;
  postalAddress: string;
  isPremium: boolean;
  isTradingHub: boolean;
  availableCount: number;
  wantedCount: number;
}

interface StoreListItemProps {
  store: MergedStore;
  cardName: string;
}

export function StoreListItem({ store, cardName }: StoreListItemProps) {
  const { serializeLGSParams } = useFacetSearchParamsContract();
  const headingId = useId();

  // The store shows once; each inventory side is its own stat and its own
  // link, so a store that both has and wants the card never hides the wants
  // route.
  const havesLink =
    store.availableCount > 0
      ? serializeLGSParams(`/x/stores/${store.slug}/haves`, { query: cardName })
      : null;
  const wantsLink =
    store.wantedCount > 0
      ? serializeLGSParams(`/x/stores/${store.slug}/wants`, { query: cardName })
      : null;

  return (
    <article className={AVAILABILITY_CARD_CLASS} aria-labelledby={headingId}>
      {/* Store name and address */}
      <div className={AVAILABILITY_HEADER_CLASS}>
        <LgsLogo
          wotcId={store.wotcId}
          storeName={store.name}
          isTradingHub={store.isTradingHub}
          size="sm"
          className="shrink-0"
          fallbackClassName="text-base-content/60"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3
              id={headingId}
              className="truncate text-sm font-semibold leading-tight"
            >
              {store.name}
            </h3>
            {store.isPremium && (
              <span
                title="Premium Store"
                className="bg-warning/15 inline-flex rounded-full p-1"
              >
                <Crown size={12} className="text-warning" />
              </span>
            )}
          </div>
          {store.postalAddress && (
            <p className="text-base-content/60 flex items-center gap-1 truncate text-xs">
              <MapPin size={10} className="shrink-0" />
              {store.postalAddress}
            </p>
          )}
        </div>
      </div>

      {/* Inventory sides */}
      <div className={AVAILABILITY_SIDES_CLASS}>
        <SideRow
          href={havesLink}
          side="haves"
          count={store.availableCount}
          place={store.name}
        />
        <SideRow
          href={wantsLink}
          side="wants"
          count={store.wantedCount}
          place={store.name}
        />
      </div>
    </article>
  );
}
